import { useEffect } from "react"

/**
 * True when the event target accepts typed characters, so bare-key shortcuts
 * can stand down. Covers inputs, textareas, selects, and any contenteditable
 * host (the Plate editor renders one).
 *
 * This used to exist three times over — in `sidebar.tsx`, in `data-table.tsx`,
 * and inline in `list-controls.tsx` — each subtly different.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
}

/**
 * True while a modal dialog, alert dialog or sheet is open.
 *
 * Radix marks the rest of the page `aria-hidden`, but a `window` listener does
 * not care about that: with a create modal open, `j`/`k`/`x` still drove the
 * table cursor behind it, and `Enter` on a focused non-input element in the
 * dialog navigated the page away to whatever row the hidden cursor was on.
 */
function modalIsOpen(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"]') !== null
}

interface Options {
  /** A bare key (no modifier) that must stand down while the user is typing. */
  bare?: boolean
  /** Set false to keep the binding live under an open dialog. */
  respectModals?: boolean
  enabled?: boolean
}

type Combo = {
  key: string
  /** Require ⌘ on macOS / Ctrl elsewhere. */
  meta?: boolean
}

/**
 * Registers one global shortcut with the shared guards.
 *
 * Every binding in `lib/shortcuts.ts` goes through here, so the cheatsheet and
 * the behaviour cannot drift apart, and a fix to the guards is a fix
 * everywhere rather than in whichever copy someone remembered.
 */
export function useShortcut(
  combo: Combo,
  handler: (event: KeyboardEvent) => void,
  { bare = false, respectModals = true, enabled = true }: Options = {},
) {
  useEffect(() => {
    if (!enabled) return

    const onKeyDown = (event: KeyboardEvent) => {
      const hasMeta = event.metaKey || event.ctrlKey
      if (combo.meta ? !hasMeta : hasMeta) return
      if (event.key.toLowerCase() !== combo.key.toLowerCase()) return
      if (bare && isTypingTarget(event.target)) return
      if (respectModals && modalIsOpen()) return

      event.preventDefault()
      handler(event)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [combo.key, combo.meta, handler, bare, respectModals, enabled])
}
